import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type FontFamily = 
  | 'Cairo' 
  | 'Arial' 
  | 'Tahoma' 
  | 'Segoe UI' 
  | 'Roboto' 
  | 'Amiri' 
  | 'Lateef'
  | 'Tajawal'
  | 'Changa'
  | 'El Messiri'
  | 'Reem Kufi'
  | 'Almarai'
  | 'Noto Sans Arabic'
  | 'Noto Naskh Arabic'
  | 'IBM Plex Sans Arabic'
  | 'Rubik'
  | 'Scheherazade New'
  | 'Harmattan'
  | 'Markazi Text'

export type FontSize = 'small' | 'medium' | 'large' | 'xlarge'

interface FontState {
  fontFamily: FontFamily
  fontSize: FontSize
  setFontFamily: (family: FontFamily) => void
  setFontSize: (size: FontSize) => void
}

const fontFamilyMap: Record<FontFamily, string> = {
  'Cairo': "'Cairo', Arial, sans-serif",
  'Arial': "Arial, sans-serif",
  'Tahoma': "Tahoma, Arial, sans-serif",
  'Segoe UI': "'Segoe UI', Tahoma, Arial, sans-serif",
  'Roboto': "'Roboto', Arial, sans-serif",
  'Amiri': "'Amiri', 'Cairo', Arial, serif",
  'Lateef': "'Lateef', 'Cairo', Arial, sans-serif",
  'Tajawal': "'Tajawal', 'Cairo', Arial, sans-serif",
  'Changa': "'Changa', 'Cairo', Arial, sans-serif",
  'El Messiri': "'El Messiri', 'Cairo', Arial, sans-serif",
  'Reem Kufi': "'Reem Kufi', 'Cairo', Arial, sans-serif",
  'Almarai': "'Almarai', 'Cairo', Arial, sans-serif",
  'Noto Sans Arabic': "'Noto Sans Arabic', 'Cairo', Arial, sans-serif",
  'Noto Naskh Arabic': "'Noto Naskh Arabic', 'Cairo', Arial, sans-serif",
  'IBM Plex Sans Arabic': "'IBM Plex Sans Arabic', 'Cairo', Arial, sans-serif",
  'Rubik': "'Rubik', 'Cairo', Arial, sans-serif",
  'Scheherazade New': "'Scheherazade New', 'Cairo', Arial, serif",
  'Harmattan': "'Harmattan', 'Cairo', Arial, sans-serif",
  'Markazi Text': "'Markazi Text', 'Cairo', Arial, serif",
}

const fontSizeMap: Record<FontSize, string> = {
  'small': '12px',
  'medium': '14px',
  'large': '16px',
  'xlarge': '18px',
}

export const useFontStore = create<FontState>()(
  persist(
    (set) => ({
      fontFamily: 'Cairo',
      fontSize: 'medium',
      setFontFamily: (family) => {
        const css = fontFamilyMap[family] || fontFamilyMap.Cairo
        set({ fontFamily: family })
        document.documentElement.style.setProperty('--font-family', css)
        document.documentElement.style.fontFamily = css
        document.body.style.fontFamily = css
        const root = document.getElementById('root')
        if (root) root.style.fontFamily = css
      },
      setFontSize: (size) => {
        const css = fontSizeMap[size] || fontSizeMap.medium
        set({ fontSize: size })
        document.documentElement.style.setProperty('--font-size-base', css)
        document.body.style.fontSize = css
      },
    }),
    {
      name: 'font-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        // Apply font settings on rehydration
        if (state?.fontFamily) {
          const css = fontFamilyMap[state.fontFamily] || fontFamilyMap.Cairo
          document.documentElement.style.setProperty('--font-family', css)
          document.documentElement.style.fontFamily = css
          document.body.style.fontFamily = css
          const root = document.getElementById('root')
          if (root) root.style.fontFamily = css
        }
        if (state?.fontSize) {
          const css = fontSizeMap[state.fontSize] || fontSizeMap.medium
          document.documentElement.style.setProperty('--font-size-base', css)
          document.body.style.fontSize = css
        }
      },
    }
  )
)

export { fontFamilyMap, fontSizeMap }

